import React, { createContext, useContext, ReactNode, useMemo } from "react";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { DocumentAssistantRetrievalContextPreviewClient } from "@services/clients/DocumentAssistantRetrievalContextPreviewClient";
import { DefaultDocumentAssistantRetrievalContextPreviewClient } from "@services/clients/DocumentAssistantRetrievalContextPreviewClient";
import { StubDocumentAssistantRetrievalContextPreviewClient } from "@services/stubs/StubDocumentAssistantRetrievalContextPreviewClient";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { requireContext } from "@contexts/requireContext";

const ContextPreviewClientContext = createContext<OptionTs<DocumentAssistantRetrievalContextPreviewClient>>(Option.none());

export const DocumentAssistantRetrievalContextPreviewClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const contextPreviewClient = useMemo<DocumentAssistantRetrievalContextPreviewClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubDocumentAssistantRetrievalContextPreviewClient();
        }
        return new DefaultDocumentAssistantRetrievalContextPreviewClient();
    }, []);

    const contextValue = useMemo(() => Option.some(contextPreviewClient), [contextPreviewClient]);

    return (
        <ContextPreviewClientContext.Provider value={contextValue}>
            {children}
        </ContextPreviewClientContext.Provider>
    );
};

export const useDocumentAssistantRetrievalContextPreviewClient = (): DocumentAssistantRetrievalContextPreviewClient =>
    requireContext(
        useContext(ContextPreviewClientContext),
        "useDocumentAssistantRetrievalContextPreviewClient must be used within a DocumentAssistantRetrievalContextPreviewClientProvider",
    );

